import React, { useEffect, useRef, useState, useContext } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as THREE from 'three';
import { ScrollContext } from './ScrollContainer';
import { IntroContext } from '../App';

gsap.registerPlugin(ScrollTrigger);

// GLSL Shaders for Organic Noise Dissolve / Morph (Ironhill Section Rebuild style)
const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform float uProgress;
  uniform vec2 uResolution;
  uniform vec3 uColor;
  uniform float uSpread;
  varying vec2 vUv;

  float Hash(vec2 p) {
    vec3 p2 = vec3(p.xy, 1.0);
    return fract(sin(dot(p2, vec3(37.1, 61.7, 12.4))) * 3758.5453123);
  }

  float noise(in vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f *= f * (3.0 - 2.0 * f);
    return mix(
      mix(Hash(i + vec2(0.0, 0.0)), Hash(i + vec2(1.0, 0.0)), f.x),
      mix(Hash(i + vec2(0.0, 1.0)), Hash(i + vec2(1.0, 1.0)), f.x),
      f.y
    );
  }

  float fbm(vec2 p) {
    float v = 0.0;
    v += noise(p * 1.0) * 0.5;
    v += noise(p * 2.0) * 0.25;
    v += noise(p * 4.0) * 0.125;
    v += noise(p * 8.0) * 0.0625;
    return v;
  }

  void main() {
    vec2 st = gl_FragCoord.xy / uResolution.xy;
    st.x *= uResolution.x / uResolution.y;

    float n = fbm(st * 3.5);

    // Liquid black rising from bottom to top as uProgress increases
    float edge = vUv.y + n * uSpread;
    float threshold = uProgress * (1.0 + uSpread);

    float d = threshold - edge;
    float pixelSize = 2.0 / uResolution.y;
    float alpha = clamp(smoothstep(-pixelSize, pixelSize, d), 0.0, 1.0);
    
    gl_FragColor = vec4(uColor, alpha);
  }
`;

const HeroSection = styled.section`
  position: relative;
  isolation: isolate;
  width: 100%;
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  min-height: 100vh;
  overflow: visible;
  box-sizing: border-box;
  user-select: none;
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: calc(100vh + clamp(100px, 10vh, 300px));
  z-index: -4;
  pointer-events: none;
  background-image: url('${process.env.PUBLIC_URL}/assets/herobg.png');
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  transition: transform 0.2s ease-out;
  will-change: transform;

  @media (max-width: 768px) {
    background-image: url('${process.env.PUBLIC_URL}/assets/herobg phoen.png');
  }
`;

const HeroInnerClip = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: -1;
  pointer-events: none;
`;

const MorphCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  max-width: 100%;
  height: calc(100vh + clamp(150px, 20vh, 300px));
  z-index: -2;
  pointer-events: none;
`;

const PetalCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  opacity: ${props => props.$isIntroComplete ? 0.9 : 0};
  transition: opacity 0.65s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: opacity;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* Hero Text Overlay Layer */
const HeroContentLayer = styled.div`
  position: absolute;
  inset: 0;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 clamp(1.2rem, 5vw, 3rem);
  box-sizing: border-box;
  z-index: 10;
  pointer-events: none;
  perspective: 1000px;
  will-change: transform, opacity, filter;
`;

const HeroTextCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(0.5rem, 1.6vh, 1.25rem);
  max-width: 900px;
  margin: 0 auto;
`;

const HeroGreeting = styled.span`
  font-family: var(--font-portfolio-mono);
  font-size: clamp(0.72rem, 1.1vw, 0.88rem);
  font-weight: 700;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: #751628;
  display: inline-block;
  opacity: 0;
  will-change: transform, opacity;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-portfolio-serif);
  font-size: clamp(3.2rem, 8.8vw, 6.8rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.015em;
  color: #5a101f;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 0.35em;
  text-shadow: 0 2px 24px rgba(117, 22, 40, 0.12);

  .hero-word {
    display: inline-flex;
    white-space: nowrap;
  }

  .hero-char {
    display: inline-block;
    opacity: 0;
    will-change: transform, opacity, filter;
  }
`;

const HeroSubtitle = styled.p`
  font-family: var(--font-portfolio-serif);
  font-size: clamp(1.05rem, 2.2vw, 1.45rem);
  font-weight: 400;
  font-style: italic;
  line-height: 1.45;
  letter-spacing: 0.01em;
  color: rgba(90, 16, 31, 0.88);
  margin: 0.25rem auto 0;
  max-width: min(90%, 650px);
  opacity: 0;
  will-change: transform, opacity, filter;
`;

const HeroScrollHint = styled.div`
  position: absolute;
  bottom: clamp(1.8rem, 4.5vh, 3.5rem);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  font-family: var(--font-portfolio-mono);
  font-size: clamp(0.65rem, 0.95vw, 0.78rem);
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: rgba(117, 22, 40, 0.75);
  opacity: 0;
  animation: floatBounce 2.5s ease-in-out infinite;

  @keyframes floatBounce {
    0%, 100% {
      transform: translate(-50%, 0);
    }
    50% {
      transform: translate(-50%, 7px);
    }
  }
`;

const Hero = () => {
  const containerRef = useRef(null);
  const bgRef = useRef(null);
  const canvasRef = useRef(null);
  const morphCanvasRef = useRef(null);
  const materialRef = useRef(null);

  const contentRef = useRef(null);
  const greetingRef = useRef(null);
  const subtitleRef = useRef(null);
  const scrollHintRef = useRef(null);

  const { containerRef: scrollContainerRef } = useContext(ScrollContext);
  const introContext = useContext(IntroContext);
  const isIntroComplete = introContext?.isIntroDone !== undefined ? introContext.isIntroDone : true;

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Name character splitting for staggered reveal
  const name = 'Yash Punmiya';
  const nameWords = name.split(' ').map((word, wIdx) => (
    <span className="hero-word" key={wIdx}>
      {word.split('').map((char, cIdx) => (
        <span className="hero-char" key={cIdx}>
          {char}
        </span>
      ))}
    </span>
  ));

  // Subtle Mouse Parallax
  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    const x = (clientX / innerWidth - 0.5) * 15;
    const y = (clientY / innerHeight - 0.5) * 10;
    setMousePos({ x, y });
  };

  // Entrance animation on mount / intro complete
  useEffect(() => {
    if (!contentRef.current || !isIntroComplete) return;

    const chars = contentRef.current.querySelectorAll('.hero-char');
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ delay: 0.15 });

      tl.fromTo(
        greetingRef.current,
        { opacity: 0, y: -25, letterSpacing: '0.1em' },
        { opacity: 1, y: 0, letterSpacing: '0.35em', duration: 0.85, ease: 'power3.out' }
      )
        .fromTo(
          chars,
          { opacity: 0, y: 40, rotateX: -30, filter: 'blur(8px)' },
          {
            opacity: 1,
            y: 0,
            rotateX: 0,
            filter: 'blur(0px)',
            stagger: 0.03,
            duration: 0.95,
            ease: 'back.out(1.4)',
          },
          '-=0.5'
        )
        .fromTo(
          subtitleRef.current,
          { opacity: 0, y: 20, filter: 'blur(5px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.85, ease: 'power3.out' },
          '-=0.45'
        )
        .fromTo(
          scrollHintRef.current,
          { opacity: 0, y: 15 },
          { opacity: 1, y: 0, duration: 0.75, ease: 'power2.out' },
          '-=0.35'
        );
    }, contentRef);

    return () => ctx.revert();
  }, [isIntroComplete]);

  // Falling Crimson Petals Canvas Effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Petal {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * -100;
        this.size = Math.random() * 8 + 6;
        this.speedX = Math.random() * 1.5 - 0.5;
        this.speedY = Math.random() * 1.2 + 0.8;
        this.rotation = Math.random() * 360;
        this.rotationSpeed = Math.random() * 1.5 - 0.75;
        this.opacity = Math.random() * 0.4 + 0.4;
        this.color = Math.random() > 0.4 ? '#751628' : '#9e233b';
      }

      update() {
        this.x += this.speedX + Math.sin(this.y * 0.008) * 0.8;
        this.y += this.speedY;
        this.rotation += this.rotationSpeed;

        if (this.y > canvas.height + 20 || this.x < -20 || this.x > canvas.width + 20) {
          this.reset();
        }
      }

      draw() {
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate((this.rotation * Math.PI) / 180);

        // Realistic organic petal geometry (elongated tapered ellipse)
        const rx = this.size * 0.55;
        const ry = this.size * 1.15;

        ctx.beginPath();
        ctx.moveTo(0, -ry);
        ctx.bezierCurveTo(rx * 1.15, -ry * 0.65, rx * 1.15, ry * 0.45, 0, ry);
        ctx.bezierCurveTo(-rx * 0.95, ry * 0.45, -rx * 0.95, -ry * 0.65, 0, -ry);
        ctx.closePath();

        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fill();

        // Delicate inner vein highlight for depth
        ctx.beginPath();
        ctx.moveTo(0, -ry * 0.7);
        ctx.quadraticCurveTo(rx * 0.25, 0, 0, ry * 0.65);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
        ctx.lineWidth = 0.8;
        ctx.stroke();

        ctx.restore();
      }
    }

    const petalCount = window.innerWidth < 768 ? 16 : 28;
    const petals = Array.from({ length: petalCount }, () => {
      const p = new Petal();
      p.y = Math.random() * canvas.height;
      return p;
    });

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      petals.forEach((petal) => {
        petal.update();
        petal.draw();
      });
      animationFrameId = requestAnimationFrame(render);
    };
    render();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Three.js Ironhill Organic Shader Canvas (Morph color matches next section bg #000000)
  useEffect(() => {
    const canvas = morphCanvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false,
    });
    renderer.setSize(width, height, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16) / 255,
            g: parseInt(result[2], 16) / 255,
            b: parseInt(result[3], 16) / 255,
          }
        : { r: 0, g: 0, b: 0 };
    };

    // Color matches the next section background: #000000 (pure black)
    const rgb = hexToRgb('#000000');
    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uProgress: { value: 0 },
        uResolution: { value: new THREE.Vector2(width, height) },
        uColor: { value: new THREE.Vector3(rgb.r, rgb.g, rgb.b) },
        uSpread: { value: 0.45 },
      },
      transparent: true,
    });

    materialRef.current = material;

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    let animationId;
    const renderLoop = () => {
      if (!materialRef.current || materialRef.current.uniforms.uProgress.value <= 1.25) {
        renderer.render(scene, camera);
      }
      animationId = requestAnimationFrame(renderLoop);
    };
    renderLoop();

    const handleResize = () => {
      if (!canvas) return;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      renderer.setSize(w, h, false);
      material.uniforms.uResolution.value.set(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  // GSAP ScrollTrigger Morph Scroll Effect & Text Exit Animation
  useEffect(() => {
    const scrollerElem = scrollContainerRef?.current || window;

    const ctx = gsap.context(() => {
      // 1. Text Exit Parallax, Scale & Fade-out as user scrolls
      if (contentRef.current) {
        gsap.to(contentRef.current, {
          y: -140,
          opacity: 0,
          scale: 0.95,
          filter: 'blur(6px)',
          ease: 'power1.out',
          scrollTrigger: {
            trigger: containerRef.current,
            scroller: scrollerElem,
            start: 'top top',
            end: '55% top',
            scrub: true,
          },
        });
      }

      // 2. Three.js Organic Noise Dissolve Morph
      ScrollTrigger.create({
        trigger: containerRef.current,
        scroller: scrollerElem,
        start: 'top top',
        end: 'bottom top',
        scrub: 0.3,
        onUpdate: (self) => {
          const progress = self.progress;

          // Update WebGL organic noise dissolve shader uniform progress dynamically as hero scrolls out
          if (materialRef.current) {
            materialRef.current.uniforms.uProgress.value = progress * 1.25;
          }
        },
      });

    }, containerRef);

    return () => ctx.revert();
  }, [scrollContainerRef]);

  return (
    <HeroSection id="hero" ref={containerRef} onMouseMove={handleMouseMove} aria-label="Hero Section">
      <BackgroundImage
        ref={bgRef}
        style={{
          transform: `translate3d(${mousePos.x * -0.4}px, ${mousePos.y * -0.4}px, 0) scale(1.02)`,
        }}
      />
      <MorphCanvas ref={morphCanvasRef} />
      <HeroInnerClip>
        <PetalCanvas ref={canvasRef} $isIntroComplete={isIntroComplete} />
      </HeroInnerClip>

      {/* Hero Animated Text Overlay Layer */}
      <HeroContentLayer ref={contentRef}>
        <HeroTextCenter>
          <HeroGreeting ref={greetingRef}>HELLO, I'M</HeroGreeting>
          <HeroTitle>{nameWords}</HeroTitle>
          <HeroSubtitle ref={subtitleRef}>
            Designer &amp; Developer crafting thoughtful digital experiences.
          </HeroSubtitle>
        </HeroTextCenter>
        <HeroScrollHint ref={scrollHintRef}>SCROLL TO EXPLORE ↓</HeroScrollHint>
      </HeroContentLayer>
    </HeroSection>
  );
};

export default Hero;
